import React, { useRef } from 'react';
import {
  CalendarIcon,
  ExpandIcon,
  IconButton,
  SignIcon,
  TextIcon,
  Chip,
  ZoominIcon,
  ZoomoutIcon,
} from '@design-system';
import { useOnClickOutside } from 'hooks/useClickOutside';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useBoolean,
} from '@chakra-ui/react';
import { translations } from 'translations';
import { Tooltip } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Popover } from 'react-tiny-popover';

export type ToolbarProps = {
  text?: (() => void) | undefined;
  date?: any;
  zoomIn: (f?: number) => void;
  zoomOut: (f?: number) => void;
  fullscreen: () => void;
  signature?: ((signerId: string) => void) | undefined;
};

export type ToolbarComponentProps = {
  /**
   * Changes the size of the button, giving it more or less padding
   */
  showFullScreen?: boolean;
  /**
   * Toolbar
   */
  toolbar: ToolbarProps;
  /**
   * Is creating
   */
  isCreating: boolean;
  /**
   * Current selected tool
   */
  selectedTool?: string;
  /**
   * Flag that enables/disabled signature field
   */
  isSignatureToolEnabled: boolean;
  /**
   * Signers array
   */
  signers: Array<any>;
  /**
   * Is calendar open
   */
  isCalendarOpen: boolean;
  /**
   * Calendar actions
   */
  calendarActions: any;
  /**
   * Date to change
   */
  dateToChange: any;
  /**
   *  Handles when user clicks outside the calendar
   */
  handleCalendarClickOutside?: (show: boolean) => void;
  /**
   * Handles calendar visibility
   */
  handleCalendarChange?: any;
};

export function Toolbar({
  toolbar,
  isCreating,
  showFullScreen = false,
  selectedTool,
  isCalendarOpen: isOpen,
  dateToChange,
  handleCalendarChange: setDateToChange,
  calendarActions: actions,
  isSignatureToolEnabled,
  signers = [],
  handleCalendarClickOutside,
}: ToolbarComponentProps) {
  const { t } = useTranslation();
  const calendarNode = useRef<HTMLDivElement>(null);

  useOnClickOutside(calendarNode, () => {
    if (handleCalendarClickOutside) {
      handleCalendarClickOutside(false);
    }
  });

  function handleSignatureField(id): void {
    toolbar.signature && toolbar.signature(id);
  }

  const showTextAndDate = true;

  return (
    <div className="flex w-[40px] flex-row gap-6 lg:flex-col lg:gap-0">
      {showFullScreen && (
        <IconButton
          color="#C4C4C4"
          onClick={toolbar.fullscreen}
          icon={<ExpandIcon />}
          variant="ghost"
        />
      )}
      <IconButton
        color="#C4C4C4"
        onClick={() => toolbar.zoomIn()}
        icon={<ZoominIcon />}
        variant="ghost"
      />
      <IconButton
        color="#C4C4C4"
        onClick={() => toolbar.zoomOut()}
        icon={<ZoomoutIcon />}
        variant="ghost"
      />

      {isCreating && (
        <>
          {isSignatureToolEnabled ? (
            signers.length > 1 ? (
              <Menu>
                <MenuButton>
                  <div className="cursor-pointer">
                    <IconButton
                      color="#C4C4C4"
                      icon={<SignIcon />}
                      variant="ghost"
                      isSelected={selectedTool === 'signature'}
                    />
                  </div>
                </MenuButton>
                <MenuList backgroundColor="#E6e6e6">
                  {signers.map((signer, index) => {
                    return (
                      <MenuItem
                        key={index}
                        onClick={() => handleSignatureField(signer.id)}>
                        <Chip color={(signer as any).color} size="sm">
                          {(signer as any).name}
                        </Chip>
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </Menu>
            ) : (
              <IconButton
                color="#C4C4C4"
                onClick={() => handleSignatureField(signers[0].id)}
                isSelected={selectedTool === 'signature'}
                icon={<SignIcon />}
                variant="ghost"
              />
            )
          ) : (
            <Tooltip
              hasArrow
              label={
                <p>{t(translations.DOCUMENT_PAGES.TOOLBAR_NO_SIGNERS)}</p>
              }>
              <div>
                <IconButton
                  isDisabled
                  color="#C4C4C4"
                  onClick={() => {}}
                  icon={<SignIcon />}
                  variant="ghost"
                />
              </div>
            </Tooltip>
          )}
        </>
      )}

      {isCreating && showTextAndDate && (
        <IconButton
          color="#C4C4C4"
          onClick={() => toolbar.text && toolbar.text()}
          icon={<TextIcon />}
          isSelected={selectedTool === 'text'}
          variant="ghost"
        />
      )}
      {isCreating && showTextAndDate && (
        <div className="relative">

          <Popover
            isOpen={isOpen}
            positions={['bottom']}
            onClickOutside={() => actions.off()}
            content={() => (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '8px',
                  background: 'white',
                }}>
                <input
                  type="date"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {

                    toolbar.date?.({
                      value: e.target.value,
                      id: dateToChange?.id,
                    });

                    setDateToChange(null);
                    actions.off();
                  }}
                />
              </div>
            )}>
            <button onClick={actions.toggle}>
              <IconButton
                color="#C4C4C4"
                icon={<CalendarIcon />}
                isSelected={selectedTool === 'date'}
                variant="ghost"
              />
            </button>
          </Popover>
        </div>
      )}
    </div>
  );
}
